import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useSelector, useDispatch } from "react-redux";
import { selectDarkMode } from "../../app/store/slices/uiSlice";
import {
  fetchDashboardData,
  selectDashboardData,
  selectDashboardLoading,
  selectDashboardError,
} from "../../app/store/slices/dashboardSlice";
import {
  selectPermissions,
  selectIsSuperAdmin,
} from "../../app/store/slices/authSlice";
import { canAccessModule } from "../../shared/utils/rbac";
import {
  CalendarDaysIcon,
  ClockIcon,
  CalendarIcon,
  UsersIcon,
  UserGroupIcon,
  BeakerIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";

// Tooltip Component using Portal
const Tooltip = ({ content, children, darkMode }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);

  const updatePosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top - 8,
        left: rect.left + rect.width / 2,
      });
    }
  };

  const handleMouseEnter = () => {
    updatePosition();
    setIsVisible(true);
  };

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setIsVisible(false)}
        className="inline-flex"
      >
        {children}
      </div>
      {isVisible && createPortal(
        <div
          className={`
            fixed px-3 py-2 text-sm rounded-lg shadow-xl
            -translate-x-1/2 -translate-y-full
            min-w-[200px] max-w-[280px] whitespace-normal pointer-events-none
            ${darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-900 text-white'}
          `}
          style={{
            zIndex: 99999,
            top: position.top,
            left: position.left,
          }}
        >
          {content}
          <div
            className={`
              absolute top-full left-1/2 -translate-x-1/2
              border-8 border-transparent
              ${darkMode ? 'border-t-gray-700' : 'border-t-gray-900'}
            `}
          />
        </div>,
        document.body
      )}
    </>
  );
};

// Animated Counter Component
const AnimatedCounter = ({ value, duration = 1000 }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (value === 0) {
      setCount(0);
      return;
    }

    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);

      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * value));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [value, duration]);

  return <span>{count.toLocaleString()}</span>;
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, color, delay = 0, trend, darkMode, tooltip }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  const colorClasses = {
    blue: {
      bg: darkMode ? 'bg-blue-900/20' : 'bg-blue-50',
      icon: 'from-blue-500 to-blue-600',
      text: darkMode ? 'text-blue-400' : 'text-blue-600',
    },
    emerald: {
      bg: darkMode ? 'bg-emerald-900/20' : 'bg-emerald-50',
      icon: 'from-emerald-500 to-green-600',
      text: darkMode ? 'text-emerald-400' : 'text-emerald-600',
    },
    violet: {
      bg: darkMode ? 'bg-violet-900/20' : 'bg-violet-50',
      icon: 'from-violet-500 to-purple-600',
      text: darkMode ? 'text-violet-400' : 'text-violet-600',
    },
    amber: {
      bg: darkMode ? 'bg-amber-900/20' : 'bg-amber-50',
      icon: 'from-amber-500 to-orange-500',
      text: darkMode ? 'text-amber-400' : 'text-amber-600',
    },
    pink: {
      bg: darkMode ? 'bg-pink-900/20' : 'bg-pink-50',
      icon: 'from-pink-500 to-rose-500',
      text: darkMode ? 'text-pink-400' : 'text-pink-600',
    },
    teal: {
      bg: darkMode ? 'bg-teal-900/20' : 'bg-teal-50',
      icon: 'from-teal-500 to-cyan-500',
      text: darkMode ? 'text-teal-400' : 'text-teal-600',
    },
  };

  const colors = colorClasses[color] || colorClasses.blue;

  return (
    <div
      className={`
        relative overflow-hidden rounded-2xl border p-6
        transition-all duration-500 ease-out
        ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
        hover:shadow-lg hover:scale-[1.02] cursor-default
      `}
    >
      {/* Background Gradient */}
      <div className={`absolute top-0 right-0 w-32 h-32 ${colors.bg} rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl`} />

      <div className="relative flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {title}
            </p>
            {tooltip && (
              <Tooltip content={tooltip} darkMode={darkMode}>
                <InformationCircleIcon className={`w-4 h-4 cursor-help ${darkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-500'}`} />
              </Tooltip>
            )}
          </div>
          <p className={`text-3xl font-bold mt-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            <AnimatedCounter value={value} duration={1200} />
          </p>
          {trend !== undefined && (
            <div className={`flex items-center mt-2 text-sm ${trend >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
              {trend >= 0 ? (
                <ArrowTrendingUpIcon className="w-4 h-4 mr-1" />
              ) : (
                <ArrowTrendingDownIcon className="w-4 h-4 mr-1" />
              )}
              <span>{Math.abs(trend)}% from last month</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl bg-gradient-to-br ${colors.icon} text-white shadow-lg`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
};



// Low Stock Table Component
const LowStockTable = ({ data, darkMode, isLoading }) => {
  return (
    <div className={`
      rounded-2xl border overflow-hidden
      ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}
    `}>
      <div className={`px-6 py-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 text-white">
            <ExclamationTriangleIcon className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Low Stock Vaccination Centers
              </h3>
              <Tooltip
                content="All centers that have vaccine units less than 150 for a particular vaccine are listed here. This helps identify centers that need immediate restocking."
                darkMode={darkMode}
              >
                <InformationCircleIcon className={`w-4 h-4 cursor-help ${darkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-500'}`} />
              </Tooltip>
            </div>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Centers with vaccine inventory below 150 units
            </p>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className={darkMode ? 'bg-gray-900' : 'bg-gray-50'}>
            <tr>
              <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Vaccine
              </th>
              <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Center
              </th>
              <th className={`px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Quantity
              </th>
           
            </tr>
          </thead>
          <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-100'}`}>
            {isLoading ? (
              [...Array(3)].map((_, i) => (
                <tr key={i}>
                  <td className="px-6 py-4">
                    <div className={`h-4 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} animate-pulse w-24`} />
                  </td>
                  <td className="px-6 py-4">
                    <div className={`h-4 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} animate-pulse w-40`} />
                  </td>
                  <td className="px-6 py-4">
                    <div className={`h-4 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} animate-pulse w-12 ml-auto`} />
                  </td>
                  <td className="px-6 py-4">
                    <div className={`h-6 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} animate-pulse w-16 mx-auto`} />
                  </td>
                </tr>
              ))
            ) : data.length > 0 ? (
              data.map((item, index) => (
                <tr
                  key={item.id || index}
                  className={`
                    transition-all duration-300
                    ${darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}
                  `}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <td className={`px-6 py-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <div className="font-medium">{item.vaccineName}</div>
                  </td>
                  <td className={`px-6 py-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    <div className="max-w-xs truncate">{item.clinicName}</div>
                  </td>
                  <td className={`px-6 py-4 text-right font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {item.totalQuantity || 0} units
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className={`px-6 py-12 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  No low stock alerts
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Loading Skeleton for Stat Cards
const StatCardSkeleton = ({ darkMode }) => (
  <div className={`
    rounded-2xl border p-6
    ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}
  `}>
    <div className="flex items-start justify-between">
      <div className="space-y-3">
        <div className={`h-4 w-24 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} animate-pulse`} />
        <div className={`h-8 w-16 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} animate-pulse`} />
      </div>
      <div className={`h-12 w-12 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} animate-pulse`} />
    </div>
  </div>
);

const Dashboard = () => {
  const dispatch = useDispatch();
  const darkMode = useSelector(selectDarkMode);
  const dashboardData = useSelector(selectDashboardData);
  const isLoading = useSelector(selectDashboardLoading);
  const error = useSelector(selectDashboardError);
  const permissions = useSelector(selectPermissions);
  const isSuperAdmin = useSelector(selectIsSuperAdmin);

  const can = (module) => canAccessModule(isSuperAdmin, permissions, module);

  useEffect(() => {
    dispatch(fetchDashboardData());
  }, [dispatch]);

  const allStatCards = [
    {
      title: 'Total Appointments',
      value: dashboardData?.appointements || 0,
      icon: CalendarDaysIcon,
      color: 'blue',
      module: 'appointments',
      tooltip: 'Total number of all appointments in the system including completed, scheduled, and cancelled.',
    },
    {
      title: 'Scheduled Appointments',
      value: dashboardData?.scheduledAppointments || 0,
      icon: ClockIcon,
      color: 'violet',
      module: 'appointments',
      tooltip: 'Appointments that are scheduled and pending completion.',
    },
    {
      title: "Today's Appointments",
      value: dashboardData?.todaysAppointments || 0,
      icon: CalendarIcon,
      color: 'emerald',
      module: 'appointments',
      tooltip: 'Number of appointments scheduled for today.',
    },
    {
      title: 'Total Parents',
      value: dashboardData?.totalParents || 0,
      icon: UsersIcon,
      color: 'pink',
      module: 'patients',
      tooltip: 'Total registered parents/guardians in the system.',
    },
    {
      title: 'Total Children',
      value: dashboardData?.totalChildren || 0,
      icon: UserGroupIcon,
      color: 'teal',
      module: 'children',
      tooltip: 'Total registered children eligible for vaccination.',
    },
    {
      title: 'Total Vaccines',
      value: dashboardData?.totalVaccines || 0,
      icon: BeakerIcon,
      color: 'amber',
      module: 'vaccines',
      tooltip: 'Total number of vaccine types available in the system.',
    },
  ];

  const statCards = allStatCards.filter((card) => can(card.module));
  const showLowStock = can('vaccine-inventory');



  if (error) {
    return (
      <div className={`min-h-[calc(100vh-120px)] flex items-center justify-center p-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        <div className={`text-center p-8 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <ExclamationTriangleIcon className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-red-400' : 'text-red-500'}`} />
          <h2 className="text-xl font-semibold mb-2">Error Loading Dashboard</h2>
          <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
      {/* Header */}
      <div className="mb-8">
        <h1 className={`text-2xl md:text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Dashboard
        </h1>
        <p className={`mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Welcome back! Here's an overview of your vaccination management system.
        </p>
      </div>

      {/* Stat Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {isLoading ? (
          [...Array(statCards.length || allStatCards.length)].map((_, i) => (
            <StatCardSkeleton key={i} darkMode={darkMode} />
          ))
        ) : (
          statCards.map((card, index) => (
            <StatCard
              key={card.title}
              title={card.title}
              value={card.value}
              icon={card.icon}
              color={card.color}
              delay={index * 100}
              darkMode={darkMode}
              tooltip={card.tooltip}
            />
          ))
        )}
      </div>

      {/* Charts and Tables Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Weekly Appointments Chart
        <div className={`
          rounded-2xl border p-6
          ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}
        `}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Weekly Appointments
              </h3>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Appointments this week
              </p>
            </div>
            <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <CalendarDaysIcon className="w-5 h-5" />
            </div>
          </div>
          <SimpleBarChart data={chartData} darkMode={darkMode} />
        </div> */}

        {/* Quick Stats Summary */}
        {/* <div className={`
          rounded-2xl border p-6
          ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}
        `}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Quick Summary
              </h3>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Key metrics at a glance
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className={`flex items-center justify-between p-4 rounded-xl ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Total Appointments</span>
              </div>
              <span className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {dashboardData?.appointements || 0}
              </span>
            </div>

            <div className={`flex items-center justify-between p-4 rounded-xl ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Today's Schedule</span>
              </div>
              <span className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {dashboardData?.todaysAppointments || 0}
              </span>
            </div>

            <div className={`flex items-center justify-between p-4 rounded-xl ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-violet-500" />
                <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Pending Appointments</span>
              </div>
              <span className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {dashboardData?.scheduledAppointments || 0}
              </span>
            </div>

            <div className={`flex items-center justify-between p-4 rounded-xl ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-amber-500" />
                <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Low Stock Alerts</span>
              </div>
              <span className={`font-bold text-amber-500`}>
                {dashboardData?.lowStockVaccinationCenters?.length || 0}
              </span>
            </div>
          </div>
        </div> */}
      </div>

      {/* Low Stock Table */}
      {showLowStock && (
        <div className="mt-6">
          <LowStockTable
            data={dashboardData?.lowStockVaccinationCenters || []}
            darkMode={darkMode}
            isLoading={isLoading}
          />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
